<template>
  <div class="episode-unlock-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span style="font-size: 18px; font-weight: bold;">解锁集数管理</span>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-input
          v-model="searchForm.username"
          placeholder="用户名"
          prefix-icon="el-icon-user"
          clearable
          style="width: 160px;"
          @keyup.enter.native="handleSearch"
        />
        <el-select
          v-model="searchForm.dramaId"
          placeholder="选择短剧"
          clearable
          filterable
          style="width: 280px;"
        >
          <el-option
            v-for="item in dramaList"
            :key="item.id"
            :label="item.title || item.name"
            :value="item.id"
          />
        </el-select>
        <el-date-picker
          v-model="searchForm.dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="解锁开始时间"
          end-placeholder="解锁结束时间"
          value-format="yyyy-MM-dd HH:mm:ss"
          :default-time="['00:00:00', '23:59:59']"
          clearable
          style="width: 360px;"
        />
        <div class="search-actions">
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
        </div>
      </div>

      <!-- 数据表格：仅短剧名称列自适应撑满，避免右侧留白 -->
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        stripe
        style="width: 100%"
      >
        <el-table-column prop="id" label="记录ID" width="80" align="center" />
        <el-table-column prop="username" label="用户名" width="130" align="center" show-overflow-tooltip />
        <el-table-column prop="userId" label="用户ID" width="88" align="center" />
        <el-table-column prop="dramaName" label="短剧名称" min-width="200" show-overflow-tooltip />
        <el-table-column prop="episodeNumber" label="集数" width="76" align="center">
          <template slot-scope="scope">
            第{{ scope.row.episodeNumber }}集
          </template>
        </el-table-column>
        <el-table-column prop="unlockType" label="解锁方式" width="108" align="center">
          <template slot-scope="scope">
            <el-tag :type="getUnlockTypeTag(scope.row.unlockType)">
              {{ getUnlockTypeText(scope.row.unlockType) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="unlockTime" label="解锁时间" width="168" align="center">
          <template slot-scope="scope">
            {{ formatDateTime(scope.row.unlockTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="expireTime" label="过期时间" width="168" align="center">
          <template slot-scope="scope">
            {{ scope.row.expireTime ? formatDateTime(scope.row.expireTime) : '永久' }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.status === 1 ? 'success' : 'info'">
              {{ scope.row.status === 1 ? '有效' : '无效' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="96" align="center">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="danger"
              icon="el-icon-delete"
              @click="handleDelete(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="pagination.pageNum"
          :page-size="pagination.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="pagination.total"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { get, del } from '@/utils/http.js'

export default {
  name: 'DramaEpisodeUnlockManagement',
  data() {
    return {
      loading: false,
      tableData: [],
      dramaList: [],
      searchForm: {
        username: '',
        dramaId: null,
        dateRange: null
      },
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  created() {
    const { username, dramaId } = this.$route.query
    if (username) {
      this.searchForm.username = username
    }
    if (dramaId) {
      this.searchForm.dramaId = Number(dramaId)
    }
    this.loadDramaList()
    this.fetchUnlockList()
  },
  methods: {
    async loadDramaList() {
      try {
        const response = await get('/admin/drama/list', { pageNum: 1, pageSize: 1000 })
        if (response && response.data) {
          this.dramaList = response.data.records || []
        }
      } catch (error) {
        console.error('加载短剧列表失败:', error)
      }
    },

    buildQueryParams() {
      const params = {
        pageNum: this.pagination.pageNum,
        pageSize: this.pagination.pageSize
      }
      if (this.searchForm.username) {
        params.username = this.searchForm.username
      }
      if (this.searchForm.dramaId) {
        params.dramaId = this.searchForm.dramaId
      }
      if (this.searchForm.dateRange && this.searchForm.dateRange.length === 2) {
        params.startDate = this.searchForm.dateRange[0]
        params.endDate = this.searchForm.dateRange[1]
      }
      return params
    },

    async fetchUnlockList() {
      this.loading = true
      try {
        const response = await get('/admin/userEpisodeUnlock/list', this.buildQueryParams())
        if (response && response.data) {
          this.tableData = response.data.records || []
          this.pagination.total = response.data.total || 0
        }
      } catch (error) {
        console.error('获取解锁记录失败:', error)
        this.$message.error(error.message || '获取解锁记录失败')
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.pageNum = 1
      this.fetchUnlockList()
    },

    resetSearch() {
      this.searchForm = {
        username: '',
        dramaId: null,
        dateRange: null
      }
      this.pagination.pageNum = 1
      this.fetchUnlockList()
    },

    handleCurrentChange(page) {
      this.pagination.pageNum = page
      this.fetchUnlockList()
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.pageNum = 1
      this.fetchUnlockList()
    },

    handleDelete(row) {
      this.$confirm(
        `确定删除用户「${row.username}」在「${row.dramaName}」第${row.episodeNumber}集的解锁记录吗？`,
        '警告',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(async () => {
        try {
          const response = await del(`/admin/userEpisodeUnlock/delete/${row.id}`)
          this.$message.success(response.msg || '删除成功')
          if (this.tableData.length === 1 && this.pagination.pageNum > 1) {
            this.pagination.pageNum -= 1
          }
          this.fetchUnlockList()
        } catch (error) {
          this.$message.error(error.message || '删除失败')
        }
      }).catch(() => {})
    },

    formatDateTime(dateTime) {
      if (!dateTime) return '-'
      return String(dateTime).replace('T', ' ')
    },

    getUnlockTypeText(type) {
      const map = {
        ad_reward: '广告解锁',
        coin: '金币解锁',
        vip: '会员解锁',
        free: '免费',
        purchase: '购买解锁'
      }
      return map[type] || type || '-'
    },

    getUnlockTypeTag(type) {
      const map = {
        ad_reward: 'warning',
        coin: 'success',
        vip: 'primary',
        free: 'info',
        purchase: 'danger'
      }
      return map[type] || ''
    }
  }
}
</script>

<style scoped>
.episode-unlock-container {
  padding: 0;
}

.search-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.search-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
